import { useEffect, useState } from "react";
import { Sparkles, Loader2, RefreshCw, History } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

// Lightweight Markdown renderer for Grace's briefing (headings, bold, bullets).
function MiniMarkdown({ text }) {
  const lines = (text || "").split("\n");
  const blocks = [];
  let list = [];
  const flush = () => { if (list.length) { blocks.push({ type: "ul", items: list }); list = []; } };
  const inline = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="font-semibold text-foreground">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>);
  lines.forEach((raw, i) => {
    const l = raw.trim();
    if (!l) { flush(); return; }
    if (l.startsWith("## ")) { flush(); blocks.push({ type: "h2", text: l.slice(3) }); }
    else if (l.startsWith("### ")) { flush(); blocks.push({ type: "h3", text: l.slice(4) }); }
    else if (l.startsWith("# ")) { flush(); blocks.push({ type: "h2", text: l.slice(2) }); }
    else if (/^[-*•]\s+/.test(l)) { list.push(l.replace(/^[-*•]\s+/, "")); }
    else { flush(); blocks.push({ type: "p", text: l, key: i }); }
  });
  flush();
  return (
    <div className="space-y-3 text-sm leading-relaxed">
      {blocks.map((b, i) => {
        if (b.type === "h2") return <h3 key={i} className="text-sm font-semibold uppercase tracking-[0.12em] text-primary mt-5 first:mt-0">{b.text}</h3>;
        if (b.type === "h3") return <h4 key={i} className="text-sm font-semibold text-foreground mt-3">{b.text}</h4>;
        if (b.type === "ul") return <ul key={i} className="space-y-1.5 pl-1">{b.items.map((it, j) => <li key={j} className="flex gap-2 text-muted-foreground"><span className="text-primary mt-1.5 h-1 w-1 rounded-full bg-primary shrink-0" /><span>{inline(it)}</span></li>)}</ul>;
        return <p key={i} className="text-muted-foreground">{inline(b.text)}</p>;
      })}
    </div>
  );
}

const MiniKpi = ({ label, value, delta }) => {
  const showDelta = delta != null && !Number.isNaN(delta) && delta !== 0;
  const up = delta > 0;
  return (
    <div className="rounded-lg border border-border bg-secondary/30 p-3">
      <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{label}</div>
      <div className="flex items-baseline gap-2 mt-1">
        <div className="font-display text-xl font-semibold">{value}</div>
        {showDelta && (
          <span className={`text-xs font-medium ${up ? "text-emerald-400" : "text-red-400"}`} data-testid={`kpi-delta-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            {up ? "▲" : "▼"} {Math.abs(Math.round(delta * 10) / 10)}
          </span>
        )}
      </div>
    </div>
  );
};

const fmtWhen = (iso) => iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

function InsightsPanel() {
  const [insight, setInsight] = useState(null);   // the briefing currently shown (full doc)
  const [history, setHistory] = useState([]);      // list of past done briefings (no report)
  const [selectedId, setSelectedId] = useState(null);
  const [autoWeekly, setAutoWeekly] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [running, setRunning] = useState(false);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/acq/insights/history?limit=30");
      setHistory(data.items || []);
      return data.items || [];
    } catch { return []; }
  };

  const load = async () => {
    try {
      const [{ data: latest }, hist, { data: sched }] = await Promise.all([
        api.get("/acq/insights/latest"),
        loadHistory(),
        api.get("/acq/insights/schedule").then((r) => r).catch(() => ({ data: { enabled: true } })),
      ]);
      setAutoWeekly(sched?.enabled !== false);
      const ins = latest.insight;
      const isRunning = ins?.status === "running";
      setRunning(isRunning);
      // Show the newest done briefing (or the running placeholder) unless the user picked one.
      if (!selectedId || isRunning) {
        setInsight(ins);
        if (ins?.status === "done") setSelectedId(ins.id);
      }
      // keep hist referenced
      void hist;
    } catch { /* ignore */ } finally { setLoaded(true); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!running) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectBriefing = async (id) => {
    setSelectedId(id);
    try {
      const { data } = await api.get(`/acq/insights/${id}`);
      setInsight(data.insight);
    } catch (e) { toast.error(apiError(e)); }
  };

  const generate = async () => {
    setRunning(true);
    try {
      await api.post("/acq/insights/generate");
      toast.success("Grace is analyzing your lead performance…");
      setSelectedId(null); // snap back to the newest run
      load();
    } catch (e) { toast.error(apiError(e)); setRunning(false); }
  };

  const toggleWeekly = async (v) => {
    setAutoWeekly(v);
    try {
      await api.post("/acq/insights/schedule", { enabled: v });
      toast.success(v ? "Grace will auto-refresh every Monday" : "Weekly auto-refresh turned off");
    } catch (e) { toast.error(apiError(e)); setAutoWeekly(!v); }
  };

  // Week-over-week deltas: compare the shown briefing to the one right before it in history.
  const idx = history.findIndex((h) => h.id === insight?.id);
  const prev = idx >= 0 ? history[idx + 1] || null : null;
  const dm = insight?.metrics, pm = prev?.metrics;
  const delta = (a, b) => (a != null && b != null) ? a - b : null;

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="ai-insights-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="text-sm font-semibold">Business Analyst Insights</div>
            <div className="text-xs text-muted-foreground">Grace Whitfield analyzes what's getting the best leads — by source &amp; market — and recommends where to focus.</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <label className="flex items-center gap-2 text-xs text-muted-foreground mr-1 cursor-pointer" title="Grace auto-runs every Monday morning">
            <Switch checked={autoWeekly} onCheckedChange={toggleWeekly} data-testid="insights-weekly-toggle" />
            Auto-refresh every Monday
          </label>
          {history.length > 0 && (
            <Select value={selectedId || ""} onValueChange={selectBriefing}>
              <SelectTrigger className="h-9 w-[190px]" data-testid="insights-history-select">
                <History className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                <SelectValue placeholder="History" />
              </SelectTrigger>
              <SelectContent>
                {history.map((h, i) => (
                  <SelectItem key={h.id} value={h.id} data-testid={`insights-history-item-${i}`}>
                    {fmtWhen(h.finished_at || h.generated_at)}{i === 0 ? " · Latest" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button onClick={generate} disabled={running} data-testid="generate-insights-btn" size="sm">
            {running ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Analyzing…</>
              : <><RefreshCw className="h-4 w-4 mr-1.5" /> {history.length ? "Refresh Insights" : "Generate Insights"}</>}
          </Button>
        </div>
      </div>

      <div className="mt-5">
        {!loaded ? <Skeleton className="h-40" />
          : running && !insight?.report ? (
            <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground" data-testid="insights-running">
              <Loader2 className="h-6 w-6 animate-spin mb-3 text-primary" />
              <p className="text-sm">Grace is reviewing your lead sources and markets — this usually takes under a minute.</p>
            </div>
          ) : insight?.status === "error" ? (
            <div className="text-sm text-muted-foreground" data-testid="insights-error">Analysis failed. Please try again.</div>
          ) : insight?.report ? (
            <div data-testid="insights-report">
              {dm && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                  <MiniKpi label="Total Leads" value={dm.total_leads} delta={pm ? delta(dm.total_leads, pm.total_leads) : null} />
                  <MiniKpi label="Win Rate" value={`${dm.overall_win_rate_pct}%`} delta={pm ? delta(dm.overall_win_rate_pct, pm.overall_win_rate_pct) : null} />
                  <MiniKpi label="Qualified / Won" value={dm.won_total} delta={pm ? delta(dm.won_total, pm.won_total) : null} />
                  <MiniKpi label="Avg Lead Score" value={dm.avg_lead_score ?? "—"} delta={pm ? delta(dm.avg_lead_score, pm.avg_lead_score) : null} />
                </div>
              )}
              {pm && <div className="text-[11px] text-muted-foreground mb-4 -mt-2">▲▼ vs previous briefing ({fmtWhen(prev.finished_at || prev.generated_at)})</div>}
              <MiniMarkdown text={insight.report} />
              <div className="text-[11px] text-muted-foreground mt-5 pt-3 border-t border-border">
                Generated by {insight.generated_by_name || "Grace Whitfield"} · {fmtWhen(insight.finished_at || insight.generated_at)}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="insights-empty">
              <p className="text-sm text-muted-foreground max-w-md">No insights yet. Click <span className="font-medium text-foreground">Generate Insights</span> and Grace will analyze which sources and markets are producing your best leads, then recommend where to focus.</p>
            </div>
          )}
      </div>
    </div>
  );
}

export default function Reports() {
  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <PageHeader title="AI Insights" subtitle="Business analyst insights on your lead performance" />
      </div>
      <InsightsPanel />
    </div>
  );
}
